<script setup lang="ts">
  import { computed } from "vue";
  import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
  import { Select } from "@/components/episto-ui/select";
  import { Input } from "@/components/ui/input";
  import ScoreMatrixField from "./CardOptionFields/ScoreMatrixField.vue";
  import OptionRow from "./CardOptionFields/OptionRow.vue";
  import OptionSection from "./CardOptionFields/OptionSection.vue";

  const props = defineProps({
    card: { type: Object, required: true },
  });

  const cardsStore = useResultsV2CardsStore();

  const THRESHOLD_OPTIONS = [
    { value: "auto", label: "Auto (means)" },
    { value: "manual", label: "Manual" },
  ];

  const thresholdMode = computed({
    get: () => {
      const mode = props.card.content?.threshold_mode;
      if (mode === "dynamic") return "auto";
      if (mode === "static") return "manual";
      return mode || "auto";
    },
    set: (val) => {
      const patch = { threshold_mode: val };
      if (val === "manual") {
        patch.satisfaction_threshold = props.card.content?.satisfaction_threshold ?? 5;
        patch.importance_threshold = props.card.content?.importance_threshold ?? 0.3;
      }
      cardsStore.updateCardLocal(props.card.id, {
        content: { ...props.card.content, ...patch },
      });
    },
  });

  const satisfactionThreshold = computed({
    get: () => props.card.content?.satisfaction_threshold ?? 5,
    set: (val) => {
      cardsStore.updateCardLocal(props.card.id, {
        content: { ...props.card.content, satisfaction_threshold: parseFloat(val) || 5 },
      });
    },
  });

  const importanceThreshold = computed({
    get: () => props.card.content?.importance_threshold ?? 0.3,
    set: (val) => {
      cardsStore.updateCardLocal(props.card.id, {
        content: { ...props.card.content, importance_threshold: parseFloat(val) || 0.3 },
      });
    },
  });
</script>

<template>
  <OptionSection
    id="data"
    title="Data">
    <ScoreMatrixField
      :card="card"
      score-label="Global satisfaction"
      score-hint="The rating or NPS question that measures overall satisfaction."
      score-placeholder="Select a rating or NPS question..."
      matrix-label="Detailed satisfaction (matrix)"
      matrix-hint="The matrix whose items are positioned on the chart, one dot per item."
      matrix-placeholder="Select a matrix..." />
  </OptionSection>

  <OptionSection
    id="display"
    title="Display"
    :count="thresholdMode === 'manual' ? 1 : 0">
    <OptionRow
      label="Thresholds"
      hint="Where the quadrant lines sit: on the means, or on values you set.">
      <Select
        :model-value="thresholdMode"
        :options="THRESHOLD_OPTIONS"
        @update:model-value="thresholdMode = $event" />
    </OptionRow>

    <template v-if="thresholdMode === 'manual'">
      <OptionRow
        label="Satisfaction"
        nested>
        <Input
          type="number"
          step="0.1"
          :model-value="satisfactionThreshold"
          @update:model-value="satisfactionThreshold = $event" />
      </OptionRow>
      <OptionRow
        label="Importance"
        nested>
        <Input
          type="number"
          step="0.01"
          :model-value="importanceThreshold"
          @update:model-value="importanceThreshold = $event" />
      </OptionRow>
    </template>
  </OptionSection>
</template>
